import {AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA} from '@angular/material/legacy-dialog';
import {FaultWritingReportConfig} from '../faults-writing-report.config';
import {MmFaultsWritingReportingService} from '../mm-fault-writing-reporting.service';
import {MediaQueryService} from '@mydocma/core';
import {getSafariMajorVersion, getIosOsMajorVersion, isIosDevice, getIosMajorVersionCapacitor} from '../../../../mm-common/utils/utils';
import {STEPPER_GLOBAL_OPTIONS} from '@angular/cdk/stepper';
import {FaultsWritingStepperComponent} from '../faults-writing-stepper/stepper/faults-writing-stepper.component';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {StepNextMethodEnum} from '../faults-writing-stepper/steps/step-next-method.enum';
import {StepButtonTypeEnum} from '../faults-writing-stepper/steps/step-button-type.enum';
import {WritingPreviewService} from '../faults-writing-stepper/services/writing-preview.service';
import {AppContainerService} from "@mydocma/app-container";
import {Logger} from '@mydocma/common/src/lib/logger';
import { NativeService } from '@mydocma/core/src/lib/native';

@UntilDestroy()
@Component({
  selector: 'mydocma-mm-writing-dialog',
  templateUrl: './faults-writing-dialog.component.html',
  styleUrls: ['./faults-writing-dialog.component.scss'],
  providers: [
    WritingPreviewService,
  ]
})
export class FaultsWritingDialogComponent implements AfterViewInit, OnDestroy {
  mobileMode: boolean;
  private static readonly ANIMATION_UNSUPPORTED_ENGINE_VERSIONS: number[] = [26];
  private static readonly KEYBOARD_VIEWPORT_DELTA_PX = 100;
  public isAnimationUnsupportedDevice: boolean;
  public readonly engineMajorVersion: number | null;
  public iosOsMajorVersion: number | null;
  private viewportHandler: (() => void) | null = null;
  private lastVvH: number | null = null;

  @ViewChild('stepperComponent', { static: false })
  private stepperComponent: FaultsWritingStepperComponent;

  constructor(@Inject(MAT_DIALOG_DATA) public dialogData: {config: FaultWritingReportConfig, service: MmFaultsWritingReportingService},
              public readonly mediaQueryService: MediaQueryService,
              public appContainerService: AppContainerService,
              private readonly previewEmailBodyService: WritingPreviewService,
              private readonly logger: Logger,
              private readonly nativeService: NativeService) {
              this.engineMajorVersion = getSafariMajorVersion();
              this.iosOsMajorVersion = getIosOsMajorVersion();
              this.isAnimationUnsupportedDevice = this.computeIsAnimationUnsupportedDevice();
  }

  async ngOnInit() {
    if(this.nativeService.isIos){
      this.iosOsMajorVersion = await getIosMajorVersionCapacitor();
      this.isAnimationUnsupportedDevice = this.computeIsAnimationUnsupportedDevice();
    }
  }

  ngAfterViewInit() {
    if (this.stepperComponent) {
      this.stepperComponent.stepperService$
        .pipe(untilDestroyed(this))
        .subscribe(service => {
          this.dialogData.service.steperService = service;
        });
    }

    if (isIosDevice() && window.visualViewport) {
      this.viewportHandler = () => {
        const vv = window.visualViewport;
        if (!vv) { return; }
        const pane = document.querySelector('.cdk-overlay-pane.mydocma-mm-writing-dialog') as HTMLElement | null;
        const container = pane ? pane.closest('.cdk-overlay-container') as HTMLElement | null : null;
        if (!container) { return; }
        const active = document.activeElement as HTMLElement | null;
        const tag = active?.tagName;
        const isTextEditingEl = tag === 'INPUT' || tag === 'TEXTAREA' || !!active?.isContentEditable;
        const vvH = Math.round(vv.height);
        const innerH = window.innerHeight;
        const clientH = document.documentElement.clientHeight;
        const delta = FaultsWritingDialogComponent.KEYBOARD_VIEWPORT_DELTA_PX;
        const keyboardOpen = isTextEditingEl && vvH < innerH - delta;
        if (keyboardOpen) {
          const top = Math.round(vv.offsetTop);
          container.style.top = `${top}px`;
          container.style.height = `${vvH}px`;
        } else {
          container.style.top = '';
          container.style.height = '';
        }

        const prevVvH = this.lastVvH;
        if (prevVvH !== null
            && prevVvH < clientH - delta
            && vvH - prevVvH >= delta
            && isTextEditingEl) {
          active?.blur();
        }
        this.lastVvH = vvH;
      };
      window.visualViewport.addEventListener('scroll', this.viewportHandler);
      window.visualViewport.addEventListener('resize', this.viewportHandler);
      document.addEventListener('focusin', this.viewportHandler, true);
      document.addEventListener('focusout', this.viewportHandler, true);
      this.viewportHandler();
    }
  }

  ngOnDestroy() {
    if (this.viewportHandler) {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('scroll', this.viewportHandler);
        window.visualViewport.removeEventListener('resize', this.viewportHandler);
      }
      document.removeEventListener('focusin', this.viewportHandler, true);
      document.removeEventListener('focusout', this.viewportHandler, true);
      const pane = document.querySelector('.cdk-overlay-pane.mydocma-mm-writing-dialog') as HTMLElement | null;
      const container = pane ? pane.closest('.cdk-overlay-container') as HTMLElement | null : null;
      if (container) {
        container.style.top = '';
        container.style.height = '';
      }
    }
  }

  private computeIsAnimationUnsupportedDevice(): boolean {
    if (!isIosDevice()) {
      return false;
    }
    const versionToCheck = this.iosOsMajorVersion ?? this.engineMajorVersion;
    if (versionToCheck == null) {
      return false;
    }
    return FaultsWritingDialogComponent.ANIMATION_UNSUPPORTED_ENGINE_VERSIONS.includes(versionToCheck);
  }

  onResize(event) {
    this.mobileMode = event.contentRect.width <= 600;
  }

  async previewEmailBody() {
    this.dialogData.service.nextSubject.next(StepNextMethodEnum.PreviewEmailBody);
  }

  async cancel() {
    this.dialogData.service.cancelReportConfiguration(this.dialogData.config);
  }

  async next() {
    try {
      switch (this.dialogData.service.ButtonDefinitions.get(StepButtonTypeEnum.Next).Method) {
        case StepNextMethodEnum.Next:
          this.dialogData.service.nextSubject.next(null);
          const nextStepName = this.dialogData.service.ButtonDefinitions.get(StepButtonTypeEnum.Next).Args;
          if (!nextStepName) {
            await this.dialogData.service.steperService.nextStep();
          } else {
            await this.dialogData.service.steperService.nextStep(nextStepName);
          }
          break;

        case StepNextMethodEnum.Print:
          this.dialogData.service.nextSubject.next(StepNextMethodEnum.Print);
          break;

        case StepNextMethodEnum.Send:
          this.dialogData.service.nextSubject.next(StepNextMethodEnum.Send);
          break;
      }
    } catch (error) {
      this.logger.error('writings (0)', error);
    }
  }
}
